import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';

export interface ReceivableRow {
    id: string;
    debtorDocument: string;
    debtorName: string;
    draweeConfirmationId?: string;
    draweeExternalId?: string;
    draweeStatus?: string;
    validationStatus?: string;
    validationStatusFormatted?: string;
    history?: Record<string, unknown>[];
    amount: number;
    status: string;
    statusFormatted?: string;
    canReject?: boolean;
    canRevokeRejection?: boolean;
    titleNumber?: string;
    nfeKey?: string;
    receivableNumber?: string;
    assignorName?: string;
    city?: string;
    state?: string;
    issueDate?: string;
    dueDate?: string;
    observation?: string;
}

export interface DebtorGroup {
    key: string;
    name: string;
    document: string;
    draweeConfirmationId?: string;
    draweeExternalId?: string;
    draweeStatus?: string;
    validationStatus?: string;
    history?: Record<string, unknown>[];
    titleCount: number;
    totalAmount: number;
    totalValidated: number;
    status: string;
    statusFormatted?: string;
    items: ReceivableRow[];
}

export default function useDraweeConfirmationPageView() {
    const { validationId, taxId } = useParams<{ validationId: string; taxId: string }>();
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [sortField, setSortField] = useState('debtor');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedDebtor, setSelectedDebtor] = useState<DebtorGroup | null>(null);
    const [apiData, setApiData] = useState<Record<string, unknown> | null>(null);
    const [allRows, setAllRows] = useState<ReceivableRow[]>([]);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    const fetchData = useCallback(async () => {
        if (!validationId) return;
        setIsLoading(true);
        try {
            const result = await collateralApi.getConfigDrawees(validationId);
            setApiData(result as Record<string, unknown>);
            setAllRows((result?.items || []) as ReceivableRow[]);
            setApiInfo({ version: result?.apiVersion || '', environment: result?.environment || '' });
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                if (error.status === 403) return navigate('/denied');
                if (error.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [validationId, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    useEffect(() => {
        if (taxId && allRows.length > 0 && !selectedDebtor) {
            const decodedTaxId = decodeURIComponent(taxId);
            const items = allRows.filter(r => r.debtorDocument === decodedTaxId);
            if (items.length > 0) {
                const first = items[0];
                setSelectedDebtor({
                    key: decodedTaxId, name: first.debtorName, document: decodedTaxId,
                    draweeConfirmationId: first.draweeConfirmationId,
                    draweeExternalId: first.draweeExternalId, draweeStatus: first.draweeStatus,
                    validationStatus: first.validationStatus, history: first.history,
                    titleCount: items.length, totalAmount: items.reduce((s, r) => s + r.amount, 0),
                    totalValidated: items.filter(r => r.status === 'VERIFIED').reduce((s, r) => s + r.amount, 0),
                    status: first.validationStatus || 'NOT_STARTED', statusFormatted: first.validationStatusFormatted || first.validationStatus || 'NOT_STARTED', items,
                });
            }
        }
    }, [taxId, allRows, selectedDebtor]);

    const groups = useMemo(() => {
        const map = new Map<string, DebtorGroup>();
        allRows.forEach(r => {
            const key = r.debtorDocument || r.debtorName;
            if (!map.has(key)) {
                map.set(key, { key, name: r.debtorName, document: r.debtorDocument, draweeConfirmationId: r.draweeConfirmationId, draweeExternalId: r.draweeExternalId, draweeStatus: r.draweeStatus, validationStatus: r.validationStatus, history: r.history, titleCount: 0, totalAmount: 0, totalValidated: 0, status: '', items: [] });
            }
            const g = map.get(key)!;
            g.titleCount++;
            g.totalAmount += r.amount;
            if (r.status === 'VERIFIED') g.totalValidated += r.amount;
            g.items.push(r);
        });
        map.forEach(g => { g.status = g.validationStatus || 'NOT_STARTED'; g.statusFormatted = g.items[0]?.validationStatusFormatted || g.status; });
        const arr = Array.from(map.values());
        const dir = sortOrder === 'ASC' ? 1 : -1;
        arr.sort((a, b) => {
            switch (sortField) {
                case 'doc': return dir * (a.document || '').localeCompare(b.document || '');
                case 'titles': return dir * (a.titleCount - b.titleCount);
                case 'amount': return dir * (a.totalAmount - b.totalAmount);
                case 'validated': return dir * (a.totalValidated - b.totalValidated);
                case 'status': return dir * (a.status || '').localeCompare(b.status || '');
                default: return dir * a.name.localeCompare(b.name);
            }
        });
        return arr;
    }, [allRows, sortField, sortOrder]);

    const filteredGroups = useMemo(() => {
        let result = groups;
        if (statusFilter) result = result.filter(g => g.status === statusFilter);
        if (search) {
            const s = search.toLowerCase();
            result = result.filter(g => g.name.toLowerCase().includes(s) || g.document.includes(s));
        }
        return result;
    }, [groups, statusFilter, search]);

    const statusCounts = useMemo(() => {
        const c: Record<string, number> = {};
        groups.forEach(g => { const s = g.status || 'NOT_STARTED'; c[s] = (c[s] || 0) + 1; });
        return c;
    }, [groups]);

    const totals = useMemo(() => ({
        amount: groups.reduce((s, g) => s + g.totalAmount, 0),
        validated: groups.reduce((s, g) => s + g.totalValidated, 0),
        count: allRows.length,
        confirmedTitles: allRows.filter(r => r.status === 'VERIFIED').length,
        uniqueDebtors: groups.length,
        validatedDebtors: groups.filter(g => g.status === 'CONFIRMED' || g.status === 'PARTIALLY_CONFIRMED').length,
    }), [allRows, groups]);

    const toggleExpand = (key: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const refresh = () => setRefreshKey(k => k + 1);

    const [isSyncing, setIsSyncing] = useState(false);

    interface SyncReceivablesResult {
        addedCount: number;
        addedReceivableIds: string[];
        removedCount: number;
        removedReceivableIds: string[];
        markedAsRemoved: { receivableId: string; reason: string }[];
    }

    const syncReceivables = useCallback(async () => {
        if (!validationId || !apiData) return;
        setIsSyncing(true);
        try {
            const operationCode = apiData['operationCode'] as string;
            if (!operationCode) {
                toast.error('Código da operação não encontrado');
                return;
            }
            const opData = await collateralApi.getOperationData(operationCode);
            const documents = opData?.documents || [];
            const receivables = documents.flatMap((d: any) =>
                (d.receivables || []).map((r: any) => ({
                    receivable_id: r.receivable_id,
                    nfe_key: d.document_id || '',
                    title_number: d.document_title || '',
                    receivable_number: r.receivable_number || '',
                    debtor_cpf_cnpj: d.debtor_cpf_cnpj || '',
                    debtor_name: d.debtor_name || '',
                    city: d.address_city || '',
                    state: d.address_state || '',
                    emission_date: d.emission_date || '',
                    due_date: r.due_date || '',
                    amount: r.amount || 0,
                }))
            ).filter((r: any) => r.receivable_id);

            if (receivables.length === 0) {
                toast.warning('Nenhum recebível encontrado na operação');
                return;
            }

            const raw = await collateralApi.syncReceivables(validationId, receivables);
            const result: SyncReceivablesResult = {
                addedCount: raw?.addedCount ?? 0,
                addedReceivableIds: raw?.addedReceivableIds ?? [],
                removedCount: raw?.removedCount ?? 0,
                removedReceivableIds: raw?.removedReceivableIds ?? [],
                markedAsRemoved: Array.isArray(raw?.markedAsRemoved) ? raw.markedAsRemoved : [],
            };

            if (result.addedCount > 0) {
                toast.success(`${result.addedCount} recebível(is) sincronizado(s)`);
            }
            if (result.removedCount > 0) {
                toast.info(`${result.removedCount} recebível(is) removido(s)`);
            }
            if (result.markedAsRemoved.length > 0) {
                toast.warning(`${result.markedAsRemoved.length} recebível(is) removido(s) da operação mas já validado(s)`);
            }
            if (result.addedCount === 0 && result.removedCount === 0 && result.markedAsRemoved.length === 0) {
                toast.info('Recebíveis já estão sincronizados');
            }
            refresh();
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                toast.error(`Erro ao sincronizar: ${error.message}`);
            } else {
                toast.error('Erro ao sincronizar recebíveis');
            }
        } finally {
            setIsSyncing(false);
        }
    }, [validationId, apiData]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectDebtor = (g: DebtorGroup) => {
        setSelectedDebtor(g);
        navigate(`/collateral/drawee-confirmation/${validationId}/receivables/drawee/${encodeURIComponent(g.document)}`, { replace: true });
    };

    const closeDebtorPanel = () => {
        setSelectedDebtor(null);
        navigate(`/collateral/drawee-confirmation/${validationId}/receivables`, { replace: true });
    };

    return {
        validationId, navigate,
        isLoading, initialLoading, apiInfo, apiData,
        statusFilter, setStatusFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        expanded, toggleExpand,
        selectedDebtor, selectDebtor, closeDebtorPanel,
        groups, filteredGroups, statusCounts, totals, allRows,
        refresh, syncReceivables, isSyncing,
    };
}
